// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\ImportBatchDetail.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { Context } from "../store/appContext";
import getApiBaseUrl from "../utils/api";

const API_URL = getApiBaseUrl();

const INITIAL_FILTERS = {
  search: "",
  material_code: "",
  supplier: "",
  date_from: "",
  date_to: "",
};

const EDITABLE_FIELDS = [
  { key: "raw_fecha", label: "Fecha", type: "date", rowKey: "movement_date" },
  { key: "raw_movimiento", label: "Movimiento", rowKey: "movement" },
  {
    key: "raw_cantidad",
    label: "Cantidad",
    type: "number",
    rowKey: "quantity",
  },
  {
    key: "raw_precio_unitario",
    label: "Precio unit.",
    type: "number",
    rowKey: "unit_price",
  },
  {
    key: "raw_precio_total",
    label: "Precio total",
    type: "number",
    rowKey: "total_price",
  },
  { key: "raw_concepto", label: "Concepto", rowKey: "concept_description" },
  { key: "raw_id_concepto", label: "ID concepto", rowKey: "concept_id" },
  { key: "raw_orden_trabajo", label: "OT", rowKey: "work_order" },
  {
    key: "raw_desc_proveedor",
    label: "Proveedor",
    rowKey: "supplier_description",
  },
];

const buildEditDraft = (row) =>
  EDITABLE_FIELDS.reduce((draft, field) => {
    const rawColumn = {
      raw_fecha: "Fecha",
      raw_movimiento: "Movimiento",
      raw_cantidad: "Cantidad",
      raw_precio_unitario: "Prec.Unit",
      raw_precio_total: "Prec.Total",
      raw_concepto: "Concepto",
      raw_id_concepto: "IDConcepto",
      raw_orden_trabajo: "OrdenTrabajo",
      raw_desc_proveedor: "DescProveedor",
    }[field.key];
    const rawValue = rawColumn ? row.raw_payload?.[rawColumn] : undefined;
    draft[field.key] = String(rawValue ?? row[field.rowKey] ?? "");
    return draft;
  }, {});

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("es-ES");
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return parsed.toLocaleString("es-ES", {
    maximumFractionDigits: 2,
  });
};

const getSortIndicator = (sortConfig, key) => {
  if (sortConfig.key !== key) {
    return "";
  }

  return sortConfig.direction === "asc" ? " ▲" : " ▼";
};

const getBalanceImpact = (classification) => {
  if (
    classification === "purchase_reposition" ||
    classification === "consumption"
  ) {
    return {
      label: "ECONÓMICO",
      className: "bg-success-subtle text-success",
    };
  }

  if (classification === "ignored") {
    return {
      label: "IGNORADO",
      className: "bg-secondary-subtle text-secondary",
    };
  }

  return {
    label: "NO ECONÓMICO",
    className: "bg-info-subtle text-info",
  };
};

const buildRowsUrl = (batchId, filters, page, perPage, sortConfig) => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });

  if (sortConfig?.key) {
    params.set("sort_by", sortConfig.key);
    params.set("sort_dir", sortConfig.direction);
  }

  Object.entries(filters).forEach(([key, value]) => {
    const normalized = String(value || "").trim();
    if (normalized) {
      params.set(key, normalized);
    }
  });

  return `${API_URL}/api/import-batches/${batchId}/staging-rows?${params.toString()}`;
};

const ImportBatchDetail = () => {
  const { batch_id: batchId } = useParams();
  const { store, actions } = useContext(Context);
  const token = store?.token || null;
  const canEditBatch = store?.role === "admin";
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    pages: 0,
    has_prev: false,
    has_next: false,
  });
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [isBatchLoading, setIsBatchLoading] = useState(true);
  const [isRowsLoading, setIsRowsLoading] = useState(true);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [isSavingRow, setIsSavingRow] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [rebuildSummary, setRebuildSummary] = useState(null);
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const rowsUrl = useMemo(
    () => buildRowsUrl(batchId, appliedFilters, page, perPage, sortConfig),
    [appliedFilters, batchId, page, perPage, sortConfig],
  );

  const sortedRows = rows;

  useEffect(() => {
    const controller = new AbortController();

    const loadBatch = async () => {
      if (!token) {
        setBatch(null);
        setIsBatchLoading(false);
        return;
      }

      setIsBatchLoading(true);

      try {
        const response = await fetch(
          `${API_URL}/api/import-batches/${batchId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setBatch(null);
          actions.showToast(
            payload?.error || payload?.msg || "No se pudo cargar el lote",
            "error",
          );
          return;
        }

        setBatch(payload.data || null);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Import batch detail load error:", error);
        setBatch(null);
        actions.showToast("No se pudo cargar el lote", "error");
      } finally {
        if (!controller.signal.aborted) {
          setIsBatchLoading(false);
        }
      }
    };

    loadBatch();

    return () => controller.abort();
  }, [batchId, token]);

  useEffect(() => {
    const controller = new AbortController();

    const loadRows = async () => {
      if (!token) {
        setRows([]);
        setIsRowsLoading(false);
        return;
      }

      setIsRowsLoading(true);

      try {
        const response = await fetch(rowsUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        const payload = await response.json().catch(() => null);

        if (response.status === 401) {
          await actions.handleApiError(response);
          return;
        }

        if (!response.ok || !payload?.success) {
          setRows([]);
          actions.showToast(
            payload?.error ||
              payload?.msg ||
              "No se pudieron cargar las filas staging",
            "error",
          );
          return;
        }

        setRows(Array.isArray(payload.data?.rows) ? payload.data.rows : []);
        setPagination((current) => ({
          ...current,
          ...(payload.data?.pagination || {}),
        }));
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Import batch staging rows load error:", error);
        setRows([]);
        actions.showToast("No se pudieron cargar las filas staging", "error");
      } finally {
        if (!controller.signal.aborted) {
          setIsRowsLoading(false);
        }
      }
    };

    loadRows();

    return () => controller.abort();
  }, [rowsUrl, token]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setDraftFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setAppliedFilters(draftFilters);
    setPage(1);
  };

  const handleReset = () => {
    setDraftFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const renderSortableHeader = (key, label, className = "") => (
    <th className={className}>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => handleSort(key)}
      >
        {label}
        {getSortIndicator(sortConfig, key)}
      </button>
    </th>
  );

  const startEditing = (row) => {
    if (!canEditBatch) {
      return;
    }

    setEditingRowId(row.id);
    setEditDraft(buildEditDraft(row));
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditDraft({});
  };

  const handleEditChange = (event) => {
    if (!canEditBatch) {
      return;
    }

    const { name, value } = event.target;
    setEditDraft((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const saveEditing = async () => {
    if (!canEditBatch || !token || !editingRowId) {
      return;
    }

    setIsSavingRow(true);

    try {
      const response = await fetch(
        `${API_URL}/api/import-batches/${batchId}/staging-rows/${editingRowId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editDraft),
        },
      );
      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        await actions.handleApiError(response);
        return;
      }

      if (!response.ok || !payload?.success) {
        actions.showToast(
          payload?.error || payload?.msg || "No se pudo guardar la fila",
          "error",
        );
        return;
      }

      setRows((current) =>
        current.map((row) => (row.id === editingRowId ? payload.data : row)),
      );
      cancelEditing();
      actions.showToast("Fila guardada correctamente", "success");
    } catch (error) {
      console.error("Import batch staging row update error:", error);
      actions.showToast("No se pudo guardar la fila", "error");
    } finally {
      setIsSavingRow(false);
    }
  };

  const requestRebuildClean = () => {
    if (!canEditBatch || !token || isRebuilding) {
      return;
    }

    setShowRebuildConfirm(true);
  };

  const rebuildClean = async () => {
    if (!canEditBatch || !token || isRebuilding) {
      return;
    }

    setShowRebuildConfirm(false);
    setIsRebuilding(true);
    setRebuildSummary(null);

    try {
      const response = await fetch(
        `${API_URL}/api/import-batches/${batchId}/rebuild-clean`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const payload = await response.json().catch(() => null);

      if (response.status === 401) {
        await actions.handleApiError(response);
        return;
      }

      if (!response.ok || !payload?.success) {
        actions.showToast(
          payload?.error || payload?.msg || "No se pudo regenerar la analítica",
          "error",
        );
        return;
      }

      setRebuildSummary(payload.data || null);
    } catch (error) {
      console.error("Import batch clean rebuild error:", error);
      actions.showToast("No se pudo regenerar la analítica", "error");
    } finally {
      setIsRebuilding(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h3 mb-1">Detalle de lote importado</h1>
          <p className="text-muted mb-0">
            Consulta de filas staging asociadas al lote #{batchId}.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {canEditBatch && (
            <button
              className="btn btn-warning"
              type="button"
              onClick={requestRebuildClean}
              disabled={isRebuilding || !batch}
            >
              <i className="bi bi-arrow-repeat me-1"></i>
              {isRebuilding ? "Regenerando..." : "Regenerar analítica del lote"}
            </button>
          )}
          <Link className="btn btn-outline-secondary" to="/import-batches">
            <i className="bi bi-arrow-left me-1"></i>
            Volver
          </Link>
        </div>
      </div>

      {rebuildSummary && (
        <div className="alert alert-success" role="alert">
          <div className="fw-semibold mb-1">Analítica regenerada</div>
          <div>
            Filas staging: {rebuildSummary.staging_rows_seen ?? 0} · insertadas:{" "}
            {rebuildSummary.rows_inserted ?? 0} · borradas:{" "}
            {rebuildSummary.clean_rows_deleted ?? 0}
          </div>
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h2 className="h5 mb-3">Resumen del lote</h2>
          {isBatchLoading && <div className="text-muted">Cargando lote...</div>}
          {!isBatchLoading && batch && (
            <div className="row g-3">
              <div className="col-md-4">
                <div className="small text-muted">Archivo</div>
                <div className="fw-semibold">{batch.source_filename}</div>
              </div>
              <div className="col-md-2">
                <div className="small text-muted">Estado</div>
                <div className="fw-semibold">{batch.status || "-"}</div>
              </div>
              <div className="col-md-2">
                <div className="small text-muted">Recibidas</div>
                <div className="fw-semibold">{batch.rows_received ?? 0}</div>
              </div>
              <div className="col-md-2">
                <div className="small text-muted">Insertadas</div>
                <div className="fw-semibold">{batch.rows_inserted ?? 0}</div>
              </div>
              <div className="col-md-2">
                <div className="small text-muted">Importado</div>
                <div className="fw-semibold">
                  {formatDateTime(batch.imported_at)}
                </div>
              </div>
              {batch.notes && (
                <div className="col-12">
                  <div className="small text-muted">Notas</div>
                  <div>{batch.notes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="h5 mb-1">Filas staging</h2>
              <p className="text-muted small mb-0">
                La ordenación por columna aplica solo a la página actual cargada.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0" htmlFor="per-page">
                Por página
              </label>
              <select
                id="per-page"
                className="form-select form-select-sm"
                value={perPage}
                onChange={handlePerPageChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <form className="row g-2 mb-3" onSubmit={handleSubmit}>
            <div className="col-md-3">
              <input
                className="form-control"
                name="search"
                placeholder="Buscar"
                value={draftFilters.search}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                name="material_code"
                placeholder="Material"
                value={draftFilters.material_code}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                name="supplier"
                placeholder="Proveedor"
                value={draftFilters.supplier}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                name="date_from"
                type="date"
                value={draftFilters.date_from}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <input
                className="form-control"
                name="date_to"
                type="date"
                value={draftFilters.date_to}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-1 d-flex gap-2">
              <button className="btn btn-primary" type="submit">
                <i className="bi bi-search"></i>
              </button>
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={handleReset}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </form>

          {isRowsLoading && (
            <div className="text-muted">Cargando filas staging...</div>
          )}

          {!isRowsLoading && rows.length === 0 && (
            <div className="alert alert-light border mb-0" role="alert">
              No hay filas staging para los filtros seleccionados.
            </div>
          )}

          {!isRowsLoading && rows.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      {renderSortableHeader("row_number", "Fila")}
                      {renderSortableHeader("movement_date", "Fecha")}
                      {renderSortableHeader("material", "Material")}
                      {renderSortableHeader("movement", "Movimiento")}
                      {renderSortableHeader("quantity", "Cantidad", "text-end")}
                      {renderSortableHeader("unit_price", "Precio unit.", "text-end")}
                      {renderSortableHeader("total_price", "Precio total", "text-end")}
                      {renderSortableHeader("supplier", "Proveedor")}
                      {renderSortableHeader("classification", "Clasificación")}
                      {renderSortableHeader("balance_impact", "Impacto balance")}
                      {canEditBatch && <th className="text-end">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => {
                      const balanceImpact = getBalanceImpact(
                        row.classification,
                      );

                      return (
                        <React.Fragment key={row.id}>
                          <tr>
                            <td>{row.row_number}</td>
                            <td>{formatDate(row.movement_date)}</td>
                            <td>
                              <div className="fw-semibold">
                                {row.material_code || "-"}
                              </div>
                              <div className="small text-muted">
                                {row.material_description || "-"}
                              </div>
                            </td>
                            <td>{row.movement || "-"}</td>
                            <td className="text-end">
                              {formatNumber(row.quantity)}
                            </td>
                            <td className="text-end">
                              {formatNumber(row.unit_price)}
                            </td>
                            <td className="text-end">
                              {formatNumber(row.total_price)}
                            </td>
                            <td>
                              <div>{row.supplier_description || "-"}</div>
                              <div className="small text-muted">
                                {row.supplier_id || ""}
                              </div>
                            </td>
                            <td>
                              {row.concept_description ||
                                row.concept_id ||
                                row.classification ||
                                "-"}
                            </td>
                            <td>
                              <span
                                className={`badge ${balanceImpact.className}`}
                              >
                                {balanceImpact.label}
                              </span>
                            </td>
                            {canEditBatch && (
                              <td className="text-end">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  type="button"
                                  onClick={() => startEditing(row)}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                          {canEditBatch && editingRowId === row.id && (
                            <tr>
                              <td colSpan="11">
                                <div className="row g-2 align-items-end">
                                  {EDITABLE_FIELDS.map((field) => (
                                    <div
                                      className="col-sm-6 col-lg-3"
                                      key={field.key}
                                    >
                                      <label className="form-label small mb-1">
                                        {field.label}
                                      </label>
                                      <input
                                        className="form-control form-control-sm"
                                        name={field.key}
                                        type={field.type || "text"}
                                        step={
                                          field.type === "number"
                                            ? "any"
                                            : undefined
                                        }
                                        value={editDraft[field.key] ?? ""}
                                        onChange={handleEditChange}
                                      />
                                    </div>
                                  ))}
                                  <div className="col-12 d-flex gap-2 justify-content-end">
                                    <button
                                      className="btn btn-primary btn-sm"
                                      type="button"
                                      onClick={saveEditing}
                                      disabled={isSavingRow}
                                    >
                                      <i className="bi bi-check-lg me-1"></i>
                                      {isSavingRow
                                        ? "Guardando..."
                                        : "Guardar"}
                                    </button>
                                    <button
                                      className="btn btn-outline-secondary btn-sm"
                                      type="button"
                                      onClick={cancelEditing}
                                      disabled={isSavingRow}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="text-muted small">
                  {pagination.total} filas · página {pagination.page} de{" "}
                  {pagination.pages || 1}
                </div>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={!pagination.has_prev}
                    onClick={() =>
                      setPage((current) => Math.max(1, current - 1))
                    }
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={!pagination.has_next}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={showRebuildConfirm}
        title="Regenerar analítica clean"
        message="Se borrarán las filas clean actuales del lote y se crearán de nuevo desde staging. Esta acción no se puede deshacer."
        confirmLabel="Regenerar"
        cancelLabel="Cancelar"
        danger
        onConfirm={rebuildClean}
        onCancel={() => setShowRebuildConfirm(false)}
      />
    </div>
  );
};

export default ImportBatchDetail;
